import {memo} from 'react';
import {Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../../../../i18n/translations';
import {OnPlannerItemClicked, PlannerDate, PlannerItem, PlannerSection} from '../planner-item';
import {areEqual, VariableSizeList as List} from 'react-window';
import memoize from 'memoize-one';
import {PlannerListActivityItem} from './planner-list-activity-item';
import {PlannerListAnniversaryItem} from './planner-list-anniversary-item';
import {PlannerListDateItem} from './planner-list-date-item';

export type PlannerListProps = {
  sections: PlannerSection[];
  onItemClicked: OnPlannerItemClicked;
  itemSelected?: PlannerItem;
};

export function PlannerList(props: PlannerListProps) {
  const {t} = useTranslation();
  const itemData = createListItemData(props.sections, props.onItemClicked, props.itemSelected);

  const getItemSize = (index: number) => {
    const item = itemData.items[index];
    // Date row needs more space to breathe(white space)
    return item.type === 'date' ? 64 : 42;
  };

  if (props.sections.length === 0) {
    return (
      <Typography sx={{textAlign: 'center', width: '100%', fontSize: 18}}>
        {t(translations.planner.body.empty)}
      </Typography>
    );
  }

  return (
    <>
      <List
        itemData={itemData}
        itemCount={itemData.items.length}
        itemSize={getItemSize}
        height={window.innerHeight - 210}
        width={'100%'}
        // useIsScrolling
      >
        {Row}
      </List>
    </>
  );
}

const Row = memo(
  ({
    index,
    style,
    isScrolling,
    ...props
  }: {
    index: number;
    isScrolling?: boolean;
    style: any;
    data: {
      items: PlannerListRow[];
      onItemClicked: OnPlannerItemClicked;
      selectedItem: PlannerItem | undefined;
    };
  }) => {
    const item = props.data.items[index];

    return (
      <div style={style}>
        {(() => {
          // --- Date Item ---
          if (item.type === 'date') {
            return <PlannerListDateItem style={{marginTop: 6}} date={item.data} />;
          }
          // --- Task Item ---
          else if (item.type === 'task') {
            const itemData = item.data as PlannerItem;

            // --- TASK: Activity Item ---
            if (itemData.type === 'activity') {
              return (
                <PlannerListActivityItem
                  item={itemData}
                  selected={props.data.selectedItem?.id === itemData.id}
                  onClick={(e) => props.data.onItemClicked(e, itemData)}
                />
              );
            }
            // --- TASK: Anniversary Item ---
            else if (itemData.type === 'anniversary') {
              return (
                <PlannerListAnniversaryItem
                  item={itemData}
                  selected={props.data.selectedItem?.id === itemData.id}
                  onClick={(e) => props.data.onItemClicked(e, itemData)}
                />
              );
            }
            // --- TASK: Unknown Item ---
            // @ts-ignore
            throw new Error(`No implementation for planner list item type: '${itemData.type}'`);
          }

          // --- Unknown Item ---
          // @ts-ignore
          throw new Error(`No implementation for planner list item type: '${item.type}'`);
        })()}
      </div>
    );
  },
  areEqual
);

// Inspiration from: https://react-window.vercel.app/#/examples/list/memoized-list-items
const createListItemData = memoize(
  (
    sections: PlannerSection[],
    onItemClicked: OnPlannerItemClicked,
    selectedItem: PlannerItem | undefined
  ) => {
    // ----------------------------------- NOTE --------------------------------------
    // We Break down the sections into individual items (date and task rows)
    // so it can perform better while using our virtualized list library (react-window)
    // -------------------------------------------------------------------------------
    const items: PlannerListRow[] = [];
    sections.forEach((section) => {
      items.push({
        type: 'date',
        data: section.date,
      });

      section.items.forEach((sectionItem) => {
        items.push({
          type: 'task',
          data: sectionItem,
        });
      });

      return items;
    });

    return {
      items,
      onItemClicked,
      selectedItem,
    };
  }
);

export type PlannerListRow = PlannerTaskListRow | PlannerDateListRow;

type PlannerBaseListRow = {
  type: 'task' | 'date';
  data: PlannerItem | PlannerDate;
};

interface PlannerTaskListRow extends PlannerBaseListRow {
  type: 'task';
  data: PlannerItem;
}

interface PlannerDateListRow extends PlannerBaseListRow {
  type: 'date';
  data: PlannerDate;
}
